import { IEventAggregator, inject, resolve } from 'aurelia';
import { EventAggregator } from 'aurelia';
import { baseUrl, websiteShortCode } from '../../environment';
import { ApiService } from '../../services/api-service';

@inject(EventAggregator)
export class Helper {
    readonly eventAggregator: IEventAggregator = resolve(IEventAggregator);

    websiteShortcodes = [
        'DS',
        'CG',
        'CX',
        'GT'
    ];

    serviceState = {};
    pendingRequests = new Map();

    removeGtagEvent = () => document.getElementById('ga4-event')?.remove();

    objectMap(obj, callback) {
        return Object.keys(obj).reduce((newObj, key) => {
            newObj[key] = callback(obj[key]);
            return newObj;
        }, {});
    }

    /**
     * Will gather all the matching keys of and object depending on the given strings.
     * @param obj Object with properties.
     * @param names Names that should be included in the keys.
     * @param exclude Names that should be excluded in the keys.
     * @returns An array containing the keys.
     */
    getPropertiesByName(obj: object, names: string[], exclude: string[] = []) {
        return Object.keys(obj)?.filter(x => this.includesWithout(x, names, exclude));
    }

    /**
     * Will gather all the variables with matching keys of an object depending on the given strings.
     * @param obj Object with properties.
     * @param names Names that should be included in the keys.
     * @param exclude Names that should be excluded in the keys.
     * @returns An array containing the values of the matching variables.
     */
    getVariablesByName(obj: object, names: string[], exclude: string[] = []) {
        return this.getPropertiesByName(obj, names, exclude).map(x => obj[x]);
    }


    resizeByNavbar(parent, navSelector = '#navigation-bar', height?) {
        const navbar = document.querySelector(navSelector);
        const app = document.querySelector(parent);
        this.changePaddingByNavbar(app, navbar, height);
        window.addEventListener('resize', () => this.changePaddingByNavbar(app, navbar, height));
    }

    changePaddingByNavbar = (app, navbar, height) => {
        app.style.marginTop = navbar.clientHeight.toString() + 'px';
        if (!height) return;
        app.style.height = `calc( ${height} - ${app.style.marginTop})`;
    };

    // ----- String Functionality -----

    /**
     * Turns a string into camel case
     * @param str the string to convert
     * @returns the string converted
     */
    camelize(str: string) {
        return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr: string) => chr.toUpperCase());
    }

    includesWithout<T>(arr: string | T[], includes: T[], excludes: T[], explicit = false) {
        const func = explicit ? 'every' : 'some';
        return this.includesBy(arr, includes, func) && this.excludeAll(arr, excludes);
    }

    includesBy = <T>(arr: string | T[], values: T[], func: string) => values[func]((x: T) => arr?.includes(x)) as boolean;

    excludeAll = <T>(arr: string | T[], values: T[]) => values?.every(x => !arr.includes(x));

    includesAll = <T>(arr: string | T[], values: T[]) => values?.every(x => arr.includes(x));

    includesSome = <T>(arr: string | T[], values: T[]) => values?.some(x => arr.includes(x));

    static toRoute = (str : string) : string => str.toLowerCase().trim().replaceAll(' ', '-');

    /**
     * Will change favicon to the given ShortCode.
     * @param shortCode ShortCode of the website.
     * @returns new favicon.
     */
    changeFavicon(shortCode: string) {
        const favicon = document.querySelector('link[rel="icon"]');
        switch (shortCode) {
            case 'DS':
                favicon?.setAttribute('href', 'favicons/ds-logo.svg');
                break;
            case 'CG':
                favicon?.setAttribute('href', 'favicons/cg-logo.svg');
                break;
            case 'CX':
                favicon?.setAttribute('href', 'favicons/cx-logo.svg');
                break;
            case 'GT':
                favicon?.setAttribute('href', 'favicons/gt-logo.svg');
                break;

            default:
                break;
        };
    };

    /**
     * Will return the base url of the website.
     * @function environmentLink - Returns the environment link.
     * @returns environment link.
     */
    get environmentLink() {
        const projectEnv = import.meta.env.VITE_ENVIRONMENT;
        const host = window.location.host;
        const parts = host.split('.');
        switch (projectEnv) {
            case 'dev':
                return `https://${parts[0].replaceAll('-blogs', '')}.${parts[1]}.${parts[2]}`;
            case 'staging':
                return `https://${parts[0].replaceAll('-blogs', '')}.${parts[1]}.${parts[2]}`;
            case 'production':
                return `https://${parts[1]}.${parts[2]}`;
            default:
                return 'Environment not found';
        }
    }

    async fetchData(api: ApiService, path: string, serviceName: string, forceFetch = false) {
        if (!this.serviceState[serviceName]) {
            this.serviceState[serviceName] = {
                responseData: null,
                isRequestPending: false,
                requestQueue: []
            };
        }

        const { responseData, isRequestPending, requestQueue } = this.serviceState[serviceName];

        if (responseData && !forceFetch) {
            return responseData;
        }

        if (isRequestPending) {
            return new Promise((resolve) => {
                requestQueue.push(resolve);
            });
        }

        try {
            this.serviceState[serviceName].isRequestPending = true;
            this.serviceState[serviceName].responseData = await api.doGet(`${path}`);
            this.processRequestQueue(serviceName);
            return this.serviceState[serviceName].responseData;
        } finally {
            this.serviceState[serviceName].isRequestPending = false;
        }
    }

    processRequestQueue(serviceName) {
        const { responseData, requestQueue } = this.serviceState[serviceName];
        while (requestQueue.length > 0) {
            const resolve = requestQueue.shift();
            if (resolve) {
                resolve(responseData);
            }
        }
    }

    clearServiceQueueState(serviceName) {
        this.serviceState[serviceName] = null;
    }

    async handlePendingRequest(component, apiCall) {
        let isRequestPending = this.pendingRequests.get(component);
        if (isRequestPending === undefined) {
            isRequestPending = false;
            this.pendingRequests.set(component, isRequestPending);
        }

        while (isRequestPending) {
            await new Promise(resolve => setTimeout(resolve, 100));
            isRequestPending = this.pendingRequests.get(component);
        }

        this.pendingRequests.set(component, true);

        try {
            return await apiCall();
        } finally {
            this.pendingRequests.set(component, false);
        }
    }

    /**
     * Will the correct links for the footer from each website.
     * @function websiteFooterLinks - Returns the environment link.
     * @returns legalSectionsLinks and websiteSectionLinks.
     */

    get websiteFooterLinks() {
        let legalSectionDropdownLinks;
        let websiteSectionDropdownLinks;

        switch (websiteShortCode()) {
            case 'CG':
                legalSectionDropdownLinks = { name: 'More', isDropdownMenu: true, showFilter: false, keyName: 'CG_FOOTER_LEGAL', menuItems: [
                    { name: 'Editorial Policy', routeName: 'editorial-policy' },
                    { name: 'Code of Ethics', routeName: 'code-of-ethics' },
                    { name: 'Complaints Policy', routeName: 'complaints-policy' },
                    { name: 'Disclaimer', routeName: 'disclaimer' },
                    { name: 'Accessibility', routeName: 'accessibility' },
                    { name: 'Modern Slavery Statement', routeName: 'modern-slavery-statement' },
                ]
                };
                websiteSectionDropdownLinks = { name: 'More', isDropdownMenu: true, showFilter: true, keyName:
                'CG_FOOTER_BUY_SELL_SWAP', menuItems: [
                    { name: 'Buy Game Assets', routeName: 'disabled' },
                    { name: 'Buy Game Currency', routeName: 'currency' },
                    { name: 'Buy Game Items', routeName: 'items' },
                    { name: 'Buy Game Accounts', routeName: 'accounts' },
                    { name: 'Buy Game Services', routeName: 'services' },
                    { name: 'Buy Game Skins', routeName: 'skins' },
                    { name: 'Buy Game Gift Card', routeName: 'gift-card' },
                    { name: 'Buy Game Unique Names', routeName: 'unique-names' },
                    { name: 'Sell Game Assets', routeName: 'disabled' },
                    { name: 'Sell Game Currency', routeName: 'sell/currency' },
                    { name: 'Sell Game Items', routeName: 'sell/items' },
                    { name: 'Sell Game Accounts', routeName: 'sell/accounts' },
                    { name: 'Sell Game Skins', routeName: 'sell/skins' },
                    { name: 'Swap Game Assets', routeName: 'disabled' },
                    //Temporarily disable per category as we don't have existing until we push new URL structure (only leaving main swap page)
                    { name: 'Swap Games', routeName: 'swap' },
                    /*{ name: 'Swap Game Currency', routeName: 'swap/currency' },
                    { name: 'Swap Game Items', routeName: 'swap/items' },
                    { name: 'Swap Game Skins', routeName: 'swap/skins' },
                    { name: 'Swap Game Unique Names', routeName: 'swap/unique-names' },*/
                ] };

                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };

            case 'CX':
                legalSectionDropdownLinks = { name: 'More', isDropdownMenu: true, showFilter: false, keyName: 'CX_FOOTER_LEGAL', menuItems: [
                    { name: 'Careers', routeName: 'careers' },
                    { name: 'Accessibility', routeName: 'accessibility' },
                    { name: 'Code of Ethics', routeName: 'code-of-ethics' },
                    { name: 'Complaints Policy', routeName: 'complaints-policy' },
                    { name: 'Disclaimer', routeName: 'disclaimer' },
                    { name: 'Editorial Policy', routeName: 'editorial-policy' },
                    { name: 'Modern Slavery Statement', routeName: 'modern-slavery-statement' },
                ]
                };
                websiteSectionDropdownLinks = { name: 'More', isDropdownMenu: true, showFilter: true, keyName: 'CX_FOOTER_BUY_SELL_SWAP', menuItems: [
                    { name: 'Buy Game Currency', routeName: 'currency' },
                    { name: 'Buy AUD to BTC', routeName: 'buy/aud-to-btc' },
                    { name: 'Buy BTC in Canada', routeName: 'buy/cad-to-btc' },
                    { name: 'Buy BTC in USA', routeName: 'buy/btc-to-usd' },
                    { name: 'Buy Crypto CX', routeName: 'buy' },
                    { name: 'Buy ETH in Canada', routeName: 'buy/cad-to-eth' },
                    { name: 'Buy ETH in USA', routeName: 'sell/usd-to-eth' },
                    { name: 'Buy LTC in USA', routeName: 'sell/usd-to-ltc' },
                    { name: 'Exchange BTC to ETH', routeName: 'swap/crypto/btc-to-eth' },
                    { name: 'Exchange BTC to LTC', routeName: 'swap/crypto/btc-to-ltc' },
                    { name: 'Exchange CAD to EUR', routeName: 'swap/fiat/cad-to-eur' },
                    { name: 'Exchange CAD to USD', routeName: 'swap/fiat/cad-to-usd' },
                    { name: 'Exchange ETH to BTC', routeName: 'swap/crypto/eth-to-btc' },
                    { name: 'Exchange EUR to CAD', routeName: 'swap/fiat/eur-to-cad' },
                    { name: 'Exchange LTC to BTC', routeName: 'swap/crypto/ltc-to-btc' },
                    { name: 'Exchange USD to CAD', routeName: 'swap/fiat/usd-to-cad' },
                    { name: 'Exchange USD to EUR', routeName: 'swap/fiat/usd-to-eur' },
                    { name: 'Sell', routeName: 'sell' },
                    { name: 'Sell BTC in Canada', routeName: 'sell/btc-to-cad' },
                    { name: 'Sell BTC in USA', routeName: 'sell/btc-to-usd' },
                    { name: 'Sell ETH in Canada', routeName: 'sell/eth-to-cad' },
                    { name: 'Sell ETH in USA', routeName: 'sell/eth-to-usd' },
                    { name: 'Sell LTC in USA', routeName: 'sell/ltc-to-usd' },
                    { name: 'Swap', routeName: 'swap' },
                    { name: 'Swap Crypto', routeName: 'swap/crypto' },
                    { name: 'Swap Fiat', routeName: 'sell/fiat' },
                ] };
                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };
            default:
                legalSectionDropdownLinks = { name: '', isDropdownMenu: false, showFilter: false, keyName: '', menuItems: [] };
                websiteSectionDropdownLinks = { name: '', isDropdownMenu: false, showFilter: false, keyName: '', menuItems: [] };
                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };
        }
    }

    /**
     * Will return the logo by the website short code.
     * @function dynamicLogo - Returns the path from the logo for the website.
     * @returns string
     */

    get dynamicLogo() {
        switch (websiteShortCode()) {
            case 'CG':
                return '/logo/logo-cg.svg';
            case 'DS':
                return '/logo/logo-ds.svg';
            case 'CX':
                return '/logo/logo-cx.svg';
            case 'GT':
                return '/logo/logo-gt.svg';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    handlePrerender404 = () => {
        const prerenderMeta = document.getElementById('prerender-404-status-code');
        if (prerenderMeta) return;
        const prerenderMetaTag = document.createElement('meta');
        prerenderMetaTag.setAttribute('id', 'prerender-404-status-code');
        prerenderMetaTag.setAttribute('name', 'prerender-status-code');
        prerenderMetaTag.setAttribute('content', '404');
        document.head.appendChild(prerenderMetaTag);
    };

    removePrerender404MetaTag = () => {
        document.getElementById('prerender-404-status-code')?.remove();
    };

    saveWindowLocalStorageValue(localStorage, value) {
        window.localStorage[localStorage] = value;
    };

    addPrerenderMetaTagForRedirect = (url) => {
        this.removePrerenderMetaTagForRedirect();

        const metaPrerenderStatusCode = document.createElement('meta');
        metaPrerenderStatusCode.setAttribute('id', 'prerender-status-code');
        metaPrerenderStatusCode.setAttribute('name', 'prerender-status-code');
        metaPrerenderStatusCode.setAttribute('content', '301');

        const metaPrerenderHeader = document.createElement('meta');
        metaPrerenderHeader.setAttribute('id', 'prerender-header');
        metaPrerenderHeader.setAttribute('content', `Location: ${baseUrl().slice(0, -1)}${url}`);

        document.head.appendChild(metaPrerenderStatusCode);
        document.head.appendChild(metaPrerenderHeader);
    };

    removePrerenderMetaTagForRedirect = () => {
        document.getElementById('prerender-header')?.remove();
        document.getElementById('prerender-status-code')?.remove();
    };
}
